import Parse from 'parse';
import { InjectionToken, Optional, Provider, SkipSelf } from '@angular/core';
import { take } from 'rxjs/operators';
import { ResourceRepository, ResourceTypes } from '@telespot/domain';
import { ParseLabelRepository, ParseResourceRepository } from '@telespot/infrastructure/parse';
import { PaginatedStateLoadingMode } from '@shared/ui';
import {
  CountResourcesQuery,
  ListResourcesQuery,
  RetrieveResourceChildrenQuery,
  RetrieveResourceDetailQuery,
  SaveLabelCommand,
} from '@protocols/use-cases';

import { LABEL_PAGINATION_MODE, LabelState } from '../../../state/label.state';
import { ResourceEditorState } from '../../../state/resource-editor.state';
import { ResourceEditorEffects, RETRIEVE_RESOURCE_DETAIL_QUERY } from '../../../effects/resource-editor.effects';
import { COUNT_RESOURCES_QUERY, LIST_RESOURCES_QUERY } from '../../../effects/resource.effects';
import { LOAD_RESOURCE_CHILDREN_QUERY } from '../../../effects/assigned-resources.effects';
import { CASCADE_SAVE_LABEL_COMMAND } from '../../../effects/save-protocol-cascade.effects';
import { saveProtocolCascadeProviders } from '../../../effects/save-protocol-cascade.providers';
import { resourceManagerProviders } from '../resource-manager/resource-manager.facade.providers';
import {
  RESOURCE_MANAGER_CONTAINER_ID,
  RESOURCE_MANAGER_NEW_RESOURCE_TYPE_LOCK,
} from '../resource-manager/resource-manager.facade';

import { ResourceEditorFacade } from './resource-editor.facade';

/**
 * The repository every read of an editing session hangs off (the detail read +
 * the catalog/children overrides below; mirror of the pipeline scope's
 * `EDITOR_PIPELINE_REPOSITORY`), declared here so this providers file never
 * reaches into `store.providers.ts`'s private repository token. Exported so an
 * outer scope (a Storybook host, a test bed) can provide a stub once and the
 * whole session resolves against it — the session factory below prefers a
 * parent-provided instance over building its own.
 */
export const RESOURCE_EDITOR_RESOURCE_REPOSITORY = new InjectionToken<ResourceRepository>(
  'RESOURCE_EDITOR_RESOURCE_REPOSITORY'
);

/**
 * Providers for ONE editing session: the editor's own state/effects/facade, the
 * detail read, and the embedded mask manager stack. Carried by every consumer
 * of a session — the route-level page scope spreads it below, and
 * `ResourceEditorDialogComponent` declares it as component providers so each
 * dialog open is a fresh session torn down with the dialog. Deliberately
 * EXCLUDES the shared tree/ledger (`AssignedResourcesState` /
 * `StagedResourcesState`) and the cascade: those belong to the outermost
 * consumer only and reach nested sessions through the dialog's injector chain.
 */
export const resourceEditorSessionProviders: Provider[] = [
  // (1) Editor scope: the per-session editor state, its load effects, the
  // facade, and the detail-read query the effects inject (over this scope's
  // repository).
  ResourceEditorState,
  ResourceEditorEffects,
  ResourceEditorFacade,
  // Prefer a parent-provided repository (the page scope's instance for a nested
  // dialog session; a stub in stories/tests); only the outermost session builds
  // the Parse one.
  {
    provide: RESOURCE_EDITOR_RESOURCE_REPOSITORY,
    useFactory: (parent: ResourceRepository | null) =>
      parent ?? new ParseResourceRepository(Parse, { useMasterKey: false }),
    deps: [[new Optional(), new SkipSelf(), RESOURCE_EDITOR_RESOURCE_REPOSITORY]],
  },
  {
    provide: RETRIEVE_RESOURCE_DETAIL_QUERY,
    useFactory: (repo: ResourceRepository) => new RetrieveResourceDetailQuery(repo),
    deps: [RESOURCE_EDITOR_RESOURCE_REPOSITORY],
  },

  // (2) Embedded manager. Spread the full manager stack first, THEN override
  // the catalog + children queries to run over THIS session's repository (later
  // entries win in Angular DI), keeping the whole session behind the one
  // stubbable token. The mask editor's catalog lists only response-type
  // resources (legacy parity): a mask contains responses. That restriction is
  // applied through the manager's `typeFilter` state — activated from the
  // `RESOURCE_MANAGER_NEW_RESOURCE_TYPE_LOCK` below, whose value doubles as this
  // manager's catalog type scope — so the queries here are plain repository
  // rebinds, not type-baking wrappers.
  ...resourceManagerProviders,
  { provide: RESOURCE_MANAGER_NEW_RESOURCE_TYPE_LOCK, useValue: ResourceTypes.response },
  {
    provide: LIST_RESOURCES_QUERY,
    useFactory: (repo: ResourceRepository) => new ListResourcesQuery(repo),
    deps: [RESOURCE_EDITOR_RESOURCE_REPOSITORY],
  },
  {
    provide: COUNT_RESOURCES_QUERY,
    useFactory: (repo: ResourceRepository) => new CountResourcesQuery(repo),
    deps: [RESOURCE_EDITOR_RESOURCE_REPOSITORY],
  },
  // Children lazy-loads of the session's nested manager run over this scope's
  // repository too (the spread above builds its own Parse instance; this
  // override keeps the whole session behind the one stubbable token).
  {
    provide: LOAD_RESOURCE_CHILDREN_QUERY,
    useFactory: (repo: ResourceRepository) => new RetrieveResourceChildrenQuery(repo),
    deps: [RESOURCE_EDITOR_RESOURCE_REPOSITORY],
  },

  // (3) The embedded manager's container id. Resolved lazily out of
  // `ResourceEditorState` on first injection — which happens only when the
  // manager renders, gated by the editor's `showManager$` (it requires
  // `containerId` to be set), so the synchronous selector snapshot (selectors
  // emit synchronously on subscribe) always sees a real id.
  {
    provide: RESOURCE_MANAGER_CONTAINER_ID,
    useFactory: (state: ResourceEditorState) => {
      let containerId = '';
      state.containerId$.pipe(take(1)).subscribe((id) => (containerId = id ?? ''));
      return containerId;
    },
    deps: [ResourceEditorState],
  },
];

/**
 * Providers for the resource editor PAGE scope, modeled on
 * `pipelineEditorProviders`. This scope is the OUTERMOST consumer of the shared
 * resource tree + ledger and the persist-now cascade. The shared tree/ledger
 * (`AssignedResourcesState` / `StagedResourcesState`) and every other cascade
 * dependency arrive via the single `saveProtocolCascadeProviders` closure — this
 * scope adds only its own editing session and the per-scope label tier.
 */
export const resourceEditorProviders: Provider[] = [
  ...resourceEditorSessionProviders,
  LabelState,
  { provide: LABEL_PAGINATION_MODE, useValue: PaginatedStateLoadingMode.CONTINUOUS },
  {
    provide: CASCADE_SAVE_LABEL_COMMAND,
    useFactory: () => new SaveLabelCommand(new ParseLabelRepository(Parse, { useMasterKey: false })),
  },

  // The cascade itself, its states, and the remaining save commands — the page
  // is the outermost consumer, so it owns this single shared closure.
  ...saveProtocolCascadeProviders,
];
